/**
 * Página Principal - Dashboard Executivo
 * Exibe gráfico de rosca com agregações por estado macro
 * Estados: Solicitadas, Em Cotação, Compra Realizada, Concluídas
 * 
 * Componente React que consome API em /api/dashboard/bi
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import { HorizontalScrollContainer } from '../components/common/HorizontalScrollContainer';
import {
  Activity,
  BadgeCheck,
  BadgeDollarSign,
  BarChart3,
  CalendarClock,
  CheckCircle,
  CircleAlert,
  CircleCheck,
  CircleCheckBig,
  CircleX,
  ClipboardList,
  Clock3,
  FileCheck2,
  FileQuestion,
  Filter,
  Handshake,
  Inbox,
  MessagesSquare,
  PackageCheck,
  PackageOpen,
  PackageSearch,
  PieChart,
  PlusCircle,
  ReceiptText,
  RefreshCw,
  ScanSearch,
  Send,
  ShieldCheck,
  ShoppingCart,
  Timer,
  TrendingUp,
  TriangleAlert,
  Truck,
  Users,
  UsersRound,
  WalletCards,
  Warehouse,
  type LucideIcon
} from 'lucide-react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import api from '../services/api';
import { obterPerfilSessao, perfilPode } from '../permissions';
import './Dashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend);

type DashboardCard = readonly [titulo: string, valor: React.ReactNode, descricao: string];

const criarCard = (titulo: string, valor: React.ReactNode, descricao: string): DashboardCard =>
  [titulo, valor, descricao];

const ICONES_INDICADORES: Record<string, LucideIcon> = {
  'Solicitadas': Send,
  'Pendentes de aprovação': Clock3,
  'Pendentes': Clock3,
  'Aprovadas': BadgeCheck,
  'Rejeitadas': CircleX,
  'Em cotação': MessagesSquare,
  'Propostas recebidas': Inbox,
  'Proposta selecionada': FileCheck2,
  'Aguardando comprovativo': ReceiptText,
  'Em trânsito': Truck,
  'Concluídas': CircleCheckBig,
  'Fornecedores activos': Handshake,
  'Utilizadores activos': Users,
  'Equipa interna': UsersRound,
  'Tempo médio de conclusão': Timer,
  'Subscrição': ShieldCheck,
  'Próxima renovação': CalendarClock,
  'Pagamentos em análise': ScanSearch,
  'Último pagamento': WalletCards,
  'Aguardando decisão': FileQuestion,
  'Compras realizadas': BadgeDollarSign,
  'Enviadas ao armazém': Warehouse,
  'Aguardando receção': PackageSearch,
  'Aguardando recepção': PackageSearch,
  'Ocorrências abertas': CircleAlert,
  'Recebidas no período': PackageCheck,
  'Recebidas sem ocorrência': PackageCheck,
  'Sem ocorrência': ShieldCheck,
  'Recebidas após ocorrência': CircleCheck,
  'Após ocorrência resolvida': CircleCheck,
  'Recebidas': PackageOpen,
  'Recebimentos concluídos': CircleCheckBig,
  'Taxa de sucesso direto': TrendingUp,
  'Taxa de ocorrências': TriangleAlert
};

interface DadosDashboard {
  sucesso: boolean;
  periodo: {
    data_inicio: string;
    data_fim: string;
  };
  indicadores: {
    total_requisicoes: number;
    requisicoes: {
      solicitadas: number;
      pendentes_aprovacao: number;
      aprovadas: number;
      rejeitadas: number;
    };
    compras: {
      em_cotacao: number;
      propostas_recebidas: number;
      propostas_aguardando_decisao: number;
      proposta_selecionada: number;
      aguardando_comprovativo: number;
      compra_realizada: number;
      em_transito: number;
      concluidas: number;
      compras_realizadas: number;
      compras_enviadas_armazem: number;
    };
    logistica: {
      em_transito: number;
      aguardando_recepcao: number;
      recebidas: number;
      ocorrencias_abertas: number;
      recebimentos_concluidos: number;
      total_aguardando_recebimento: number;
      total_com_ocorrencia_aberta: number;
      total_recebido_periodo: number;
      recebidos_sem_ocorrencia: number;
      recebidos_apos_ocorrencia_resolvida: number;
      taxa_sucesso_recebimento: number;
      taxa_ocorrencias: number;
      tempo_medio_recebimento_horas: number | null;
    };
    operacao: {
      fornecedores_activos_vinculados: number;
      utilizadores_activos: number;
      equipa_interna: number;
      tempo_medio_conclusao_horas: number | null;
    };
    financeiro: {
      subscricao: null | { status: string; plano?: string | null; valor_mensal?: number; moeda?: string };
      proxima_renovacao: string | null;
      ultimo_pagamento: null | { valor_pago: number; moeda: string; status: string; data_pagamento?: string };
      pagamentos_pendentes_analise: number;
    };
    solicitante: null | {
      solicitadas: number;
      pendentes: number;
      aprovadas: number;
      rejeitadas: number;
      em_cotacao: number;
      concluidas: number;
    };
  };
  resumo: {
    total_requisicoes: number;
    solicitacoes: number;
    em_cotacao: number;
    em_transito: number;
    concluidas: number;
    nao_classificadas?: number;
    valor_total_estimado: number;
    valor_total_comprado: number;
    valor_medio_requisicao: number;
  };
  grafico_rosca: {
    titulo: string;
    total: number;
    series: Array<{
      nome: string;
      valor: number;
      percentual: string;
      cor: string;
      status: string;
    }>;
  };
  tendencias: {
    requisicoes_ultimos_7_dias: number;
    requisicoes_ultimos_30_dias: number;
    tendencia: string;
  };
  moeda: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const perfil = obterPerfilSessao();
  const podeCriarRequisicao = perfilPode(perfil, 'requisicoes.criar');
  const podeAprovar = perfilPode(perfil, 'aprovacoes.decidir');
  const podeComprar = perfilPode(perfil, 'comprador.fecharCompra');
  const podeReceber = perfilPode(perfil, 'armazem.receber');
  const [dados, setDados] = useState<DadosDashboard | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  // Define datas padrão ao montar o componente
  useEffect(() => {
    const hoje = new Date();
    const passado = new Date(hoje.getTime() - 30 * 24 * 60 * 60 * 1000);

    setDataFim(hoje.toISOString().split('T')[0]);
    setDataInicio(passado.toISOString().split('T')[0]);
  }, []);

  // Busca dados do dashboard quando mudam as datas
  useEffect(() => {
    if (!dataInicio || !dataFim) return;

    const carregarDados = async () => {
      try {
        setCarregando(true);
        setErro(null);

        const resposta = await api.get('/dashboard/bi', {
          params: {
            data_inicio: dataInicio,
            data_fim: dataFim
          }
        });

        if (resposta.data.sucesso) {
          setDados(resposta.data);
        } else {
          setErro('Erro ao carregar dados do dashboard');
        }
      } catch (erro: any) {
        console.error('Erro ao buscar dashboard:', erro);
        setErro(
          erro.response?.data?.mensagem ||
          'Erro ao buscar dados do dashboard'
        );
      } finally {
        setCarregando(false);
      }
    };

    carregarDados();
  }, [dataInicio, dataFim]);

  // Configura dados do gráfico de rosca
  const seriesComLabels = () => dados?.grafico_rosca.series || [];

  const configurarGrafico = () => {
    if (!dados) return null;

    const series = seriesComLabels();
    const labels = series.map(s => s.nome);
    const valores = series.map(s => s.valor);
    const cores = series.map(s => s.cor);

    return {
      labels,
      datasets: [
        {
          data: valores,
          backgroundColor: cores,
          borderColor: '#000000',
          borderWidth: 2,
          hoverOffset: 10
        }
      ]
    };
  };

  const opcoes: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#FFFFFF',
          font: { size: 14, weight: 500 },
          padding: 20,
          usePointStyle: true
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const valor = context.parsed || 0;
            const percentual =
              (
                (valor /
                  Math.max(dados!.grafico_rosca.total || dados!.resumo.total_requisicoes, 1)) *
                100
              ).toFixed(1) + '%';
            return `${label}: ${valor} (${percentual})`;
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#D4AF37',
        bodyColor: '#FFFFFF',
        borderColor: '#D4AF37',
        borderWidth: 1,
        padding: 12,
        displayColors: true
      }
    }
  };

  if (carregando) {
    return (
      <div className="dashboard-container">
        <div className="spinner-container">
          <div className="spinner"></div>
          <p>Carregando dados do dashboard...</p>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="dashboard-container">
        <div className="alerta alerta-erro">
          <strong>Erro:</strong> {erro}
        </div>
      </div>
    );
  }

  if (!dados) {
    return (
      <div className="dashboard-container">
        <div className="alerta alerta-info">
          Nenhum dado disponível para o período selecionado.
        </div>
      </div>
    );
  }

  const dataCurta = (data?: string | null) => data ? new Intl.DateTimeFormat('pt-AO').format(new Date(data)) : 'Sem registo';
  const valorMoeda = (valor?: number, moeda = dados.moeda) => `${moeda} ${Math.round(Number(valor || 0)).toLocaleString('pt-PT')}`;
  const indicador = dados.indicadores;
  const cardsGerente: DashboardCard[] = [
    ['Solicitadas', indicador.requisicoes.solicitadas, 'Status SOLICITADO'],
    ['Pendentes de aprovação', indicador.requisicoes.pendentes_aprovacao, 'Aprovação PENDENTE'],
    ['Aprovadas', indicador.requisicoes.aprovadas, 'Aprovação APROVADA'],
    ['Rejeitadas', indicador.requisicoes.rejeitadas, 'Aprovação REJEITADA'],
    ['Em cotação', indicador.compras.em_cotacao, 'Status EM_COTACAO'],
    ['Propostas recebidas', indicador.compras.propostas_recebidas, 'Cotação com propostas'],
    ['Proposta selecionada', indicador.compras.proposta_selecionada, 'Status PROPOSTA_SELECIONADA'],
    ['Aguardando comprovativo', indicador.compras.aguardando_comprovativo, 'Status AGUARDANDO_COMPROVATIVO'],
    ['Em trânsito', indicador.compras.em_transito, 'Status EM_TRANSITO'],
    ['Concluídas', indicador.compras.concluidas, 'Status CONCLUIDO'],
    ['Fornecedores activos', indicador.operacao.fornecedores_activos_vinculados, 'Vínculos ATIVO'],
    ['Utilizadores activos', indicador.operacao.utilizadores_activos, 'Utilizadores ATIVO'],
    ['Equipa interna', indicador.operacao.equipa_interna, 'Perfis operacionais'],
    ['Recebidas sem ocorrência', indicador.logistica.recebidos_sem_ocorrencia, 'Sucesso direto no período'],
    ['Recebidas após ocorrência', indicador.logistica.recebidos_apos_ocorrencia_resolvida, 'Ocorrência resolvida'],
    ['Taxa de sucesso direto', `${indicador.logistica.taxa_sucesso_recebimento}%`, 'Sem ocorrência / finalizadas'],
    ['Taxa de ocorrências', `${indicador.logistica.taxa_ocorrencias}%`, 'Com ocorrência / finalizadas'],
    ...(indicador.operacao.tempo_medio_conclusao_horas !== null
      ? [criarCard('Tempo médio de conclusão', `${indicador.operacao.tempo_medio_conclusao_horas}h`, 'Requisições concluídas')]
      : []),
    ...(indicador.financeiro.subscricao
      ? [criarCard('Subscrição', indicador.financeiro.subscricao.status, indicador.financeiro.subscricao.plano || 'Plano activo')]
      : []),
    ['Próxima renovação', dataCurta(indicador.financeiro.proxima_renovacao), 'Subscrição'],
    ...(indicador.financeiro.ultimo_pagamento
      ? [criarCard('Último pagamento', valorMoeda(indicador.financeiro.ultimo_pagamento.valor_pago, indicador.financeiro.ultimo_pagamento.moeda), indicador.financeiro.ultimo_pagamento.status)]
      : []),
    ['Pagamentos em análise', indicador.financeiro.pagamentos_pendentes_analise, 'Status PENDENTE_ANALISE']
  ];
  const cardsComprador: DashboardCard[] = [
    ['Em cotação', indicador.compras.em_cotacao, 'Status EM_COTACAO'],
    ['Propostas recebidas', indicador.compras.propostas_recebidas, 'Cotações com proposta'],
    ['Aguardando decisão', indicador.compras.propostas_aguardando_decisao, 'Propostas ENVIADA'],
    ['Compras realizadas', indicador.compras.compras_realizadas, 'Compra finalizada'],
    ['Aguardando comprovativo', indicador.compras.aguardando_comprovativo, 'Status AGUARDANDO_COMPROVATIVO'],
    ['Enviadas ao armazém', indicador.compras.compras_enviadas_armazem, 'Status EM_TRANSITO']
  ];
  const cardsArmazem: DashboardCard[] = [
    ['Aguardando receção', indicador.logistica.total_aguardando_recebimento, 'Compra realizada ou em trânsito'],
    ['Ocorrências abertas', indicador.logistica.total_com_ocorrencia_aberta, 'Pendentes de tratamento'],
    ['Recebidas no período', indicador.logistica.total_recebido_periodo, 'Recebimentos finalizados'],
    ['Sem ocorrência', indicador.logistica.recebidos_sem_ocorrencia, 'Sucesso direto'],
    ['Após ocorrência resolvida', indicador.logistica.recebidos_apos_ocorrencia_resolvida, 'Finalizadas após tratamento'],
    ['Taxa de sucesso direto', `${indicador.logistica.taxa_sucesso_recebimento}%`, 'Sem ocorrência / finalizadas'],
    ['Taxa de ocorrências', `${indicador.logistica.taxa_ocorrencias}%`, 'Com ocorrência / finalizadas'],
    ['Em trânsito', indicador.logistica.em_transito, 'Status EM_TRANSITO'],
    ['Aguardando recepção', indicador.logistica.aguardando_recepcao, 'Status EM_TRANSITO'],
    ['Recebidas', indicador.logistica.recebidas, 'Status CONCLUIDO'],
    ['Ocorrências abertas', indicador.logistica.ocorrencias_abertas, 'Registos no período'],
    ['Recebimentos concluídos', indicador.logistica.recebimentos_concluidos, 'Status CONCLUIDO']
  ];
  const cardsSolicitante: DashboardCard[] = indicador.solicitante ? [
    ['Solicitadas', indicador.solicitante.solicitadas, 'Minhas requisições'],
    ['Pendentes', indicador.solicitante.pendentes, 'Aprovação PENDENTE'],
    ['Aprovadas', indicador.solicitante.aprovadas, 'Aprovação APROVADA'],
    ['Rejeitadas', indicador.solicitante.rejeitadas, 'Aprovação REJEITADA'],
    ['Em cotação', indicador.solicitante.em_cotacao, 'Status EM_COTACAO'],
    ['Concluídas', indicador.solicitante.concluidas, 'Status CONCLUIDO']
  ] : [];
  const cardsPorPerfil = perfil === 'GERENTE_EMPRESA'
    ? cardsGerente
    : perfil === 'COMPRADOR'
      ? cardsComprador
      : perfil === 'FIEL_ARMAZEM'
        ? cardsArmazem
        : perfil === 'SOLICITANTE'
          ? cardsSolicitante
          : cardsGerente;

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <h1>Painel Executivo - Akzição</h1>
        <p className="subtitulo">Visão geral de requisições e compras</p>
      </header>

      {/* Controles de Data */}
      <section className="secao-filtros">
        <div className="filtro-titulo">
          <Filter size={18} aria-hidden="true" />
          <span>Período</span>
        </div>
        <div className="filtro-grupo">
          <label htmlFor="data-inicio">Data Inicial:</label>
          <input
            id="data-inicio"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
          />
        </div>
        <div className="filtro-grupo">
          <label htmlFor="data-fim">Data Final:</label>
          <input
            id="data-fim"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
          />
        </div>
      </section>

      {/* Cards de Resumo */}
      <section className="secao-resumo">
        <div className="cartao-resumo">
          <div className="card-title-icon"><ClipboardList size={18} aria-hidden="true" /><h4>Total de Requisições</h4></div>
          <p className="numero-grande">{dados.resumo.total_requisicoes}</p>
          <p className="periodo">{dados.periodo.data_inicio} a {dados.periodo.data_fim}</p>
        </div>
        {cardsPorPerfil.map(([titulo, valor, descricao], indice) => {
          const IconeIndicador = ICONES_INDICADORES[titulo];
          return (
            <div className={`cartao-resumo ${indice % 4 === 0 ? 'destaque-solicitacoes' : indice % 4 === 1 ? 'destaque-aprovacao' : indice % 4 === 2 ? 'destaque-comprados' : 'destaque-entregues'}`} key={`${titulo}-${indice}`}>
              <div className="card-title-icon">
                <IconeIndicador size={18} strokeWidth={2} aria-hidden="true" />
                <h4>{titulo}</h4>
              </div>
              <p className="numero-grande texto-metrica">{valor}</p>
              <p className="descricao">{descricao}</p>
            </div>
          );
        })}
      </section>

      {/* Gráfico de Rosca */}
      <section className="secao-grafico">
        <div className="cartao">
          <h3 className="section-title-icon"><PieChart size={20} aria-hidden="true" />{dados.grafico_rosca.titulo}</h3>
          <div className="grafico-wrapper">
            <Doughnut
              data={configurarGrafico()!}
              options={opcoes}
            />
          </div>
        </div>

        {/* Legenda Detalhada */}
        <div className="cartao legenda-detalhada">
          <h3 className="section-title-icon"><BarChart3 size={20} aria-hidden="true" />Detalhes por Estado</h3>
          <HorizontalScrollContainer>
            <table className="tabela-legenda">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Quantidade</th>
                  <th>Percentual</th>
                </tr>
              </thead>
              <tbody>
                {seriesComLabels().map((item) => (
                  <tr key={item.status}>
                    <td>
                      <span
                        className="ponto-cor"
                        style={{ backgroundColor: item.cor }}
                      ></span>
                      {item.nome}
                    </td>
                    <td>{item.valor}</td>
                    <td className="percentual">{item.percentual}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HorizontalScrollContainer>
        </div>
      </section>

      {/* Estatísticas Adicionais */}
      <section className="secao-estatisticas">
        <div className="cartao">
          <h3 className="section-title-icon"><Activity size={20} aria-hidden="true" />Estatísticas</h3>
          <div className="grid-2">
            <div className="item-stat">
              <label>Total de requisições</label>
              <p className="valor-destaque">
                {dados.resumo.total_requisicoes}
              </p>
            </div>

            <div className="item-stat">
              <label>Valor comprado</label>
              <p className="valor-destaque">
                {dados.moeda} {Math.round(dados.resumo.valor_total_comprado).toLocaleString('pt-PT')}
              </p>
            </div>

            <div className="item-stat">
              <label>Últimos 7 dias</label>
              <p className="valor-destaque">
                {dados.tendencias.requisicoes_ultimos_7_dias} requisições
              </p>
            </div>

            <div className="item-stat">
              <label>Últimos 30 dias</label>
              <p className="valor-destaque">
                {dados.tendencias.requisicoes_ultimos_30_dias} requisições
              </p>
            </div>
          </div>
        </div>

        <div className="cartao">
          <h3 className="section-title-icon"><PackageCheck size={20} aria-hidden="true" />Tendência</h3>
          <p className={`tendencia-${dados.tendencias.tendencia.toLowerCase()}`}>
            {dados.tendencias.tendencia}
          </p>
          <p className="descricao">
            {dados.tendencias.tendencia === 'CRESCENTE'
              ? 'Volume de requisições em crescimento'
              : 'Volume de requisições estável'}
          </p>
        </div>
      </section>

      {/* Botões de Ação */}
      <section className="secao-acoes">
        {podeCriarRequisicao && (
          <button className="botao botao-primario" type="button" onClick={() => navigate('/requisicoes')}>
            <PlusCircle size={18} aria-hidden="true" />
            Nova Requisição
          </button>
        )}
        {podeAprovar && (
          <button className="botao botao-secundario" type="button" onClick={() => navigate('/aprovacoes')}>
            <CheckCircle size={18} aria-hidden="true" />
            Aprovações pendentes
          </button>
        )}
        {podeComprar && (
          <button className="botao botao-secundario" type="button" onClick={() => navigate('/comprador')}>
            <ShoppingCart size={18} aria-hidden="true" />
            Fluxo do comprador
          </button>
        )}
        {podeReceber && (
          <button className="botao botao-secundario" type="button" onClick={() => navigate('/armazem')}>
            <PackageCheck size={18} aria-hidden="true" />
            Recebimento
          </button>
        )}
        <button className="botao botao-secundario" type="button" onClick={() => window.location.reload()}>
          <RefreshCw size={18} aria-hidden="true" />
          Atualizar
        </button>
      </section>
    </div>
  );
};

export default Dashboard;
